import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { Student } from '@/context/OverviewContext';
import { useSelectedStudent } from '@/context/SelectedStudentContext';
import { useTasklist } from '@/context/TasklistContext';

interface StudentListProps {
  students: Student[];
  onSelect?: (student: Student) => void;
  /** When false, hides the incomplete-task count badge (e.g. teacher preview / dummy students). */
  showTaskBadge?: boolean;
}

const StudentList: React.FC<StudentListProps> = ({ students, onSelect, showTaskBadge = true }) => {
  
  const { selectedStudent, setSelectedStudent } = useSelectedStudent();
  const { tasklistData, tasklistTopicStatus } = useTasklist();
  const navigate = useNavigate();
  const location = useLocation();

  const getTaskCountForStudent = (accountId: number | string): number => {
    const completedChecklistIds = tasklistTopicStatus
      .filter(
        item =>
          String(item.accountId) === String(accountId) &&
          item.status === 'true'
      )
      .map(item => item.checklistId);

    const incompleteTasks = tasklistData.filter(
      item => !completedChecklistIds.includes(item.checklistId)
    );

    return incompleteTasks.length;
  };

  const handleStudentClick = (student: Student) => {
    setSelectedStudent(student);
    if (onSelect) {
      onSelect(student);
    }
    navigate('/overview');
  };

  return (
    <div className="student-list">
      {students.map((student) => {
        const taskCount = getTaskCountForStudent(student.accountId);
        const isActive = selectedStudent?.accountId === student.accountId;

        return (
          <div
            key={student.accountId} 
            className={`student-row ${isActive ? 'active' : ''}`}
            onClick={() => handleStudentClick(student)}
            style={{ cursor: 'pointer' }}
            >
            <div className="student-info">
              <div className="student-details">
                <div className="student-name">
                  {student.firstName} {student.lastName}
                </div>
              {showTaskBadge && taskCount > 0 && (
                <span className="student-badge">
                  {taskCount} {taskCount === 1 ? 'task' : 'tasks'}
                </span>
              )}
              </div>
              <div className="student-meta">
                {student.schoolShortName} | Grade {student.gradeId}
              </div>
            </div>
            <svg
              className="student-arrow-icon"
              width="25"
              height="25"
              viewBox="0 0 25 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.68555 12.1858H19.6855M19.6855 12.1858L12.6855 5.18579M19.6855 12.1858L12.6855 19.1858"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        );
      })}
    </div>
  );
};

export default StudentList;